import {subscribeToRouteChanged} from "../eventBus/route/routeEventBus";
import {inAppAnalyticsService} from "../inAppAnalyticsService";
import {matchScreen} from "./screenCatalogMatcher";
import type {Unsubscribe} from "../eventBus/common";

/**
 * Emits the GA4 `in_app_page_view` event on every screen navigation.
 *
 * Subscribes to the route-changed event bus, resolves the concrete location path
 * to a screen-catalog entry (screen_name / screen_class), and forwards the event
 * through the native bridge. Routes with no catalog entry (e.g. redirect helpers
 * like /last-page-used, or the dev playground) are intentionally dropped.
 *
 * Per the no-module-level-side-effects rule, this module performs no work at
 * import time; the entry point calls init() and may call the returned disposer.
 */
class PageViewTracking {
  #unsubscribe: Unsubscribe | null = null;
  #lastScreenClass: string | null = null;

  init(): Unsubscribe {
    // Idempotent: a second init() replaces the first subscription.
    this.dispose();

    this.#unsubscribe = subscribeToRouteChanged(({locationPath}) => {
      const screen = matchScreen(locationPath);
      if (!screen) {
        // No catalogued screen for this path; do not emit a page view. Reset the
        // dedupe marker so that navigating from a catalogued screen, through an
        // uncatalogued route, and back to the same screen still emits a fresh
        // view (the trip through another route is a genuine re-navigation).
        this.#lastScreenClass = null;
        return;
      }

      // Dedupe consecutive page views for the same screen (e.g. a redirect that
      // lands on the same screen, or a query-only change) to avoid double counts.
      if (screen.screenClass === this.#lastScreenClass) {
        return;
      }
      this.#lastScreenClass = screen.screenClass;

      inAppAnalyticsService.onInAppPage_view({
        screenName: screen.screenName,
        screenClass: screen.screenClass,
      });
    });

    return () => this.dispose();
  }

  dispose(): void {
    if (this.#unsubscribe) {
      this.#unsubscribe();
      this.#unsubscribe = null;
    }
    this.#lastScreenClass = null;
  }
}

export const pageViewTracking = new PageViewTracking();
